// src/pages/MemberBenefits.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import FullBleed from '../components/FullBleed';
import StageFrame from '../components/StageFrame';
import bg from '../assets/card.png';

axios.defaults.withCredentials = true;

const STATIC_CONTENT = {
  zh: {
    title: '齊文會員福利',
    benefits: '會員福利',
    membership: '會員層級',
    back: '返回會員卡',
    benefit1: {
      title: '① 非營業時間使用空間權（依會員資格開放）',
      desc: '可於深夜、清晨等一般顧客無法入場的時段使用空間。',
      annual: '需於營業時間內消費入場，可待至打烊後自助離場。',
      monthly: '週一至週五全時段自由進出。',
    },
    benefit2: {
      title: '② 朋友同行優惠',
      desc: '會員攜伴入場，可享特別折扣或同行優惠方案。',
      annual: '攜伴享同價入場。',
      monthly: '攜伴享資深會員價入場。',
    },
    benefit3: {
      title: '③ 活動參加優惠',
      desc: '會員可享齊文活動之專屬折扣、優先報名或限定席次。',
    },
    benefit4: {
      title: '④ 專屬飲品零食',
      desc: '享用會員專屬飲品優惠、自助零食與限定選項。',
    },
    benefit5: {
      title: '⑤ 辦公小物使用',
      desc: '如充電線、毛毯、基本文具、簡易器材等皆可免費或優惠借用。',
    },
    annual: {
      title: '年會員',
      fee: '年費：',
      feeValue: '$699 / 年（首年）',
      renewal: '續約或推薦價：',
      renewalValue: '$500 / 年',
      entry: '入場方式',
      entryValue: '前 2 小時 $179，加時每小時 $40，上限 $379',
      feature: '特點',
      featureValue: '每消費 100 元可累積一點（以單筆發票金額計算，不可跨單合併），滿二十點即可升級資深年會員',
    },
    premium: {
      title: '資深會員',
      subtitle: '（年會員消費滿 20 點）',
      entry: '入場方式',
      entryValue: '前 2 小時 $175，加時每小時 $25，上限 $300',
    },
    monthly: {
      title: '月會員',
      fee: '月費：',
      feeValue: '$4,000 / 月（約每日 $180）',
      entry: '入場方式',
      entryItems: [
        '週一至週五 24 小時皆可入場，不須額外費用',
        '假日入場則依資深會員價收費',
        '前 2 小時 $175，加時每小時 $25，上限 $300',
        '攜伴價：同上資深會員計價',
      ],
      special: '限定福利',
      specialItems: [
        '會員每月享營業時間內訪客 30 分鐘免費訪問權限',
        '（訪客不可使用自駐區與其他空間設備）',
        '月會員會籍結束若續約成年會員可享續約價：$500 / 年，並升級為資深會員',
      ],
    },
  },
  en: {
    title: 'Singularity Member Benefits',
    benefits: 'Member Benefits',
    membership: 'Membership Tiers',
    back: 'Back to Member Card',
    benefit1: {
      title: '① After-Hours Space Access (Based on Membership)',
      desc: 'Access to the space during late night and early morning hours when regular customers cannot enter.',
      annual: 'Annual Members: Must enter during business hours, can stay until after closing and leave independently.',
      monthly: 'Monthly Members: Full access Monday to Friday, 24 hours.',
    },
    benefit2: {
      title: '② Friend Benefits',
      desc: 'Members bringing guests can enjoy special discounts or companion benefit packages.',
      annual: 'Annual Members: Guests pay the same price as members.',
      monthly: 'Monthly Members: Guests pay premium member rates.',
    },
    benefit3: {
      title: '③ Event Special Offer',
      desc: 'Members enjoy exclusive discounts, priority registration, or reserved seats for Qiwen events.',
    },
    benefit4: {
      title: '④ Exclusive Drinks & Snacks',
      desc: 'Enjoy member-exclusive beverage discounts, self-service snacks, and limited options.',
    },
    benefit5: {
      title: '⑤ Free Equipment',
      desc: 'Free or discounted use of items such as charging cables, blankets, basic stationery, and simple equipment.',
    },
    annual: {
      title: 'Annual Membership',
      fee: 'Annual Fee:',
      feeValue: '$699 / year (first year)',
      renewal: 'Renewal or Referral Price:',
      renewalValue: '$500 / year',
      entry: 'Entry Method',
      entryValue: 'First 2 hours $179, additional $40/hour, maximum $379',
      feature: 'Features',
      featureValue: 'Earn 1 point for every $100 spent (calculated per invoice, cannot combine across invoices). Upgrade to Premium Annual Membership after 20 points.',
    },
    premium: {
      title: 'Premium Membership',
      subtitle: '(Annual members with 20+ points)',
      entry: 'Entry Method',
      entryValue: 'First 2 hours $175, additional $25/hour, maximum $300',
    },
    monthly: {
      title: 'Monthly Membership',
      fee: 'Monthly Fee:',
      feeValue: '$4,000 / month (approx. $180/day)',
      entry: 'Entry Method',
      entryItems: [
        '24-hour access Monday to Friday, no additional fees',
        'Weekend entry charged at premium member rates',
        'First 2 hours $175, additional $25/hour, maximum $300',
        'Guest rate: Same as premium member pricing',
      ],
      special: 'Special Benefits',
      specialItems: [
        'Members enjoy 30 minutes of free visitor access during business hours per month',
        '(Visitors cannot use co-working areas or other space facilities)',
        'If renewing as annual member after monthly membership ends, enjoy renewal price: $500/year and upgrade to Premium Membership',
      ],
    },
  },
};

function hasRemotePayload(data) {
  if (!data || data.ok === false) return false;
  const pt = data.pageTitle || {};
  const sec = data.sections || {};
  const zh = sec.zh || [];
  const en = sec.en || [];
  return !!(String(pt.zh || '').trim() || String(pt.en || '').trim() || zh.length || en.length);
}

export default function MemberBenefits() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('zh');
  const [remote, setRemote] = useState(null);
  const [remoteReady, setRemoteReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/member-benefits')
      .then(({ data }) => {
        if (!cancelled) setRemote(data);
      })
      .catch(() => {
        if (!cancelled) setRemote(null);
      })
      .finally(() => {
        if (!cancelled) setRemoteReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  const useRemote = useMemo(
    () => remoteReady && hasRemotePayload(remote),
    [remote, remoteReady],
  );

  const loadError = useMemo(
    () => remoteReady && (!remote || remote.ok === false),
    [remote, remoteReady],
  );

  const t = STATIC_CONTENT[lang];

  return (
    <FullBleed bg={bg}>
      <StageFrame
        designW={1080}
        designH={1920}
        maxPcWidth={440}
        overlay={({ px }) => (
          <>
            <style>{`
              .mb-wrap {
                position: absolute;
                left: ${px(80)}px;
                right: ${px(80)}px;
                top: ${px(220)}px;
                bottom: ${px(150)}px;
                overflow-y: auto;
                color: #111;
                background: #f0dfd4;
                border-radius: ${px(28)}px;
                padding: ${px(40)}px ${px(44)}px;
                box-shadow: 0 ${px(10)}px ${px(34)}px rgba(0,0,0,.18);
                border: ${px(3)}px solid rgba(0,0,0,.2);
              }
              .mb-lang-btn {
                position: absolute;
                top: ${px(40)}px;
                right: ${px(40)}px;
                padding: ${px(12)}px ${px(20)}px;
                font-size: ${px(32)}px;
                font-weight: 700;
                border: ${px(2)}px solid #5e3a1a;
                border-radius: ${px(12)}px;
                background: rgba(255,255,255,.9);
                color: #5e3a1a;
                cursor: pointer;
                z-index: 10;
                box-shadow: 0 ${px(4)}px ${px(12)}px rgba(0,0,0,.15);
              }
              .mb-lang-btn:active {
                transform: translateY(${px(1)}px);
              }
              .mb-title {
                font-size: ${px(88)}px;
                font-weight: 900;
                margin: 0 0 ${px(40)}px;
                text-align: center;
                color: #5e3a1a;
                font-family: "CreamFont","CSong3HK",system-ui,-apple-system,sans-serif;
              }
              .mb-section-title {
                font-size: ${px(60)}px;
                font-weight: 900;
                color: #5e3a1a;
                margin: ${px(40)}px 0 ${px(28)}px;
                border-left: ${px(8)}px solid #5e3a1a;
                padding-left: ${px(20)}px;
              }
              .mb-benefit-card {
                background: rgba(255,255,255,.6);
                border-radius: ${px(20)}px;
                padding: ${px(28)}px;
                margin-bottom: ${px(24)}px;
              }
              .mb-benefit-title {
                font-size: ${px(48)}px;
                font-weight: 900;
                color: #7c2d12;
                margin: 0 0 ${px(16)}px;
              }
              .mb-benefit-text {
                font-size: ${px(36)}px;
                line-height: 1.6;
                color: #374151;
                margin: ${px(12)}px 0;
              }
              .mb-markdown {
                font-size: ${px(36)}px;
                line-height: 1.65;
                color: #374151;
              }
              .mb-markdown > *:first-child { margin-top: 0; }
              .mb-markdown > *:last-child { margin-bottom: 0; }
              .mb-markdown p { margin: ${px(16)}px 0; }
              .mb-markdown h1, .mb-markdown h2, .mb-markdown h3, .mb-markdown h4 {
                color: #5e3a1a;
                font-weight: 900;
                margin: ${px(28)}px 0 ${px(14)}px;
                line-height: 1.3;
              }
              .mb-markdown h1 { font-size: ${px(52)}px; }
              .mb-markdown h2 { font-size: ${px(48)}px; }
              .mb-markdown h3 { font-size: ${px(44)}px; }
              .mb-markdown h4 { font-size: ${px(40)}px; }
              .mb-markdown ul, .mb-markdown ol {
                margin: ${px(16)}px 0;
                padding-left: ${px(40)}px;
              }
              .mb-markdown li { margin-bottom: ${px(8)}px; }
              .mb-markdown strong { color: #111827; }
              .mb-markdown a { color: #1d4ed8; word-break: break-word; }
              .mb-markdown blockquote {
                margin: ${px(16)}px 0;
                padding: ${px(12)}px ${px(20)}px;
                border-left: ${px(6)}px solid rgba(94,58,26,.45);
                background: rgba(255,255,255,.45);
                border-radius: 0 ${px(12)}px ${px(12)}px 0;
                color: #4b5563;
              }
              .mb-markdown hr {
                border: none;
                border-top: ${px(2)}px solid rgba(94,58,26,.25);
                margin: ${px(24)}px 0;
              }
              .mb-markdown pre {
                margin: ${px(16)}px 0;
                padding: ${px(16)}px;
                border-radius: ${px(12)}px;
                background: rgba(17,24,39,.06);
                overflow-x: auto;
                font-size: ${px(28)}px;
              }
              .mb-markdown code {
                font-family: ui-monospace, monospace;
                font-size: 0.9em;
                padding: ${px(2)}px ${px(8)}px;
                border-radius: ${px(6)}px;
                background: rgba(17,24,39,.08);
              }
              .mb-markdown pre code {
                padding: 0;
                background: transparent;
                font-size: inherit;
              }
              .mb-markdown img {
                max-width: 100%;
                height: auto;
                border-radius: ${px(16)}px;
                display: block;
                margin: ${px(20)}px auto;
              }
              .mb-benefit-list {
                font-size: ${px(34)}px;
                line-height: 1.7;
                color: #4b5563;
                margin: ${px(12)}px 0;
                padding-left: ${px(28)}px;
              }
              .mb-benefit-list li {
                margin-bottom: ${px(8)}px;
              }
              .mb-member-card {
                background: rgba(255,255,255,.8);
                border-radius: ${px(24)}px;
                padding: ${px(32)}px;
                margin-bottom: ${px(28)}px;
                border: ${px(3)}px solid rgba(94,58,26,.3);
              }
              .mb-member-title {
                font-size: ${px(56)}px;
                font-weight: 900;
                color: #5e3a1a;
                margin: 0 0 ${px(20)}px;
              }
              .mb-member-subtitle {
                font-size: ${px(32)}px;
                color: #6b7280;
                margin-bottom: ${px(20)}px;
                font-style: italic;
              }
              .mb-member-price {
                font-size: ${px(38)}px;
                font-weight: 700;
                color: #7c2d12;
                margin-bottom: ${px(20)}px;
              }
              .mb-member-label {
                color: #5e3a1a;
                font-size: ${px(40)}px;
                font-weight: 800;
                margin-top: ${px(16)}px;
                margin-bottom: ${px(8)}px;
              }
              .mb-member-detail {
                font-size: ${px(34)}px;
                line-height: 1.6;
                color: #374151;
                margin: ${px(8)}px 0;
              }
              .mb-member-list {
                font-size: ${px(34)}px;
                line-height: 1.7;
                color: #374151;
                margin: ${px(8)}px 0;
                padding-left: ${px(28)}px;
              }
              .mb-member-list li {
                margin-bottom: ${px(6)}px;
              }
              .mb-back-btn {
                position: fixed;
                left: ${px(140)}px;
                right: ${px(140)}px;
                bottom: ${px(60)}px;
                height: ${px(120)}px;
                font-size: ${px(54)}px;
                font-weight: 900;
                border: ${px(2)}px solid #111;
                border-radius: ${px(12)}px;
                background: #f0dfd4;
                color: #111;
                cursor: pointer;
                z-index: 10;
                box-shadow: 0 ${px(6)}px ${px(18)}px rgba(0,0,0,.08);
                font-family: "CreamFont","CSong3HK",system-ui,-apple-system,sans-serif;
                padding: ${px(12)}px ${px(16)}px;
              }
              .mb-back-btn:active {
                transform: translateY(1px);
              }
            `}</style>

            {/* 語言切換按鈕 */}
            <button
              className="mb-lang-btn"
              onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')}
              onMouseDown={(e) => e.currentTarget.style.transform = `translateY(${px(1)}px)`}
              onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {lang === 'zh' ? 'EN' : '中文'}
            </button>

            <div className="mb-wrap">
              {!remoteReady ? (
                <p className="mb-benefit-text" style={{ textAlign: 'center', paddingTop: 32 }}>
                  {lang === 'zh' ? '載入中…' : 'Loading…'}
                </p>
              ) : useRemote ? (
                <>
                  {String(remote?.pageTitle?.[lang] || '').trim() ? (
                    <h1 className="mb-title">{String(remote.pageTitle[lang]).trim()}</h1>
                  ) : null}
                  <section>
                    {(remote?.sections?.[lang] || []).length === 0 ? (
                      <p className="mb-benefit-text" style={{ textAlign: 'center' }}>
                        {lang === 'zh' ? '（尚無內容）' : '(No content yet)'}
                      </p>
                    ) : (
                      (remote.sections?.[lang] || []).map((s, idx) => (
                        <div key={`${idx}-${s.sortOrder}`} className="mb-benefit-card">
                          {String(s.title || '').trim() ? (
                            <h3 className="mb-benefit-title">{s.title}</h3>
                          ) : null}
                          <div className="mb-markdown">
                            <ReactMarkdown
                              remarkPlugins={[remarkBreaks]}
                              rehypePlugins={[rehypeSanitize]}
                              components={{
                                a: ({ node, ...props }) => (
                                  <a {...props} target="_blank" rel="noopener noreferrer" />
                                ),
                                img: ({ node, ...props }) => (
                                  <img {...props} alt={props.alt ?? ''} loading="lazy" decoding="async" />
                                ),
                              }}
                            >
                              {s.body || ''}
                            </ReactMarkdown>
                          </div>
                        </div>
                      ))
                    )}
                  </section>
                </>
              ) : loadError ? (
                <p className="mb-benefit-text" style={{ textAlign: 'center', paddingTop: 32 }}>
                  {lang === 'zh' ? '無法載入權益內容' : 'Failed to load benefits.'}
                </p>
              ) : (
                <p className="mb-benefit-text" style={{ textAlign: 'center', paddingTop: 32 }}>
                  {lang === 'zh' ? '尚無權益說明' : 'No benefits have been configured yet.'}
                </p>
              )}
            </div>

            {/* 返回按鈕 */}
            <button
              className="mb-back-btn"
              onClick={() => navigate('/')}
            >
              💳 {t.back}
            </button>
          </>
        )}
      />
    </FullBleed>
  );
}
